import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const getCardType = (number) => {
  if (!number) return "Unknown";

  // Clean spaces/dashes from card number
  const cleaned = number.replace(/\D/g, "");

  if (cleaned.startsWith("4")) return "Visa";
  if (cleaned.startsWith("5")) return "MasterCard";
  if (cleaned.startsWith("34") || cleaned.startsWith("37")) return "Amex";
  if (cleaned.startsWith("6")) {
    if (cleaned.startsWith("60") || cleaned.startsWith("6521") || cleaned.startsWith("65")) {
      return "RuPay";
    }
    return "Discover";
  }

  return "Unknown";
};


// gradient color mapping for different cards
const cardGradients = {
  Visa: "linear-gradient(135deg, #1a2980, #26d0ce)",       // Blue-green
  MasterCard: "linear-gradient(135deg, #ff512f, #dd2476)", // Red-pink
  Amex: "linear-gradient(135deg, #3a1c71, #d76d77, #ffaf7b)", // Purple-orange
  Discover: "linear-gradient(135deg, #654ea3, #eaafc8)",   // Purple-pink
  RuPay: "linear-gradient(135deg, #1ba94c, #0f8140)",      // ✅ Green shades
  Unknown: "linear-gradient(135deg, #1976d2, #42a5f5)",    // Default blue
};


const CardPreviewModal = ({ open, onClose, cards }) => {
  const [flipped, setFlipped] = useState(false);

  if (!cards.length) return null;

  const card = cards[cards.length - 1];
  const cardType = getCardType(card.cardNumber);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          border: "4px solid #00AAE7",
        },
      }}
    >
      {/* Modal Title */}
      <Box
        sx={{
          position: "relative",
          borderBottom: "1px solid #DFDFDF",
          pb: 2,
        }}
      >
        <Typography
          sx={{
            px: 3,
            pt: 2,
            fontSize: "17px",
            color: "#1A1F36",
            fontWeight: "bold",
          }}
        >
          Card Preview
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent>
        <Typography
          sx={{ textAlign: "center", mb: 2, fontWeight: "bold", color: "#555" }}
        >
          Click on card to flip and see details
        </Typography>

        <Box
          sx={{
            perspective: "1000px",
            width: "350px",
            height: "220px",
            margin: "auto",
            cursor: "pointer",
          }}
          onClick={() => setFlipped(!flipped)}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              transition: "transform 0.6s",
              transformStyle: "preserve-3d",
              transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
            }}
          >
            {/* Front Side */}
            <Box
              sx={{
                position: "absolute",
                width: "100%",
                height: "100%",
                backfaceVisibility: "hidden",
                background: cardGradients[cardType],
                color: "white",
                borderRadius: "12px",
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "start",
                flexDirection: "column",
              }}
            >
              <Typography sx={{ fontSize: "18px", fontWeight: "bold" }}>
                {cardType}
              </Typography>

              <Box>
                <Typography sx={{ fontSize: "14px", fontWeight: "500", mt: 2 }}>
                  Card Number
                </Typography>
                <Typography
                  sx={{ letterSpacing: 2, fontSize: "16px", fontWeight: "500" }}
                >
                  {card.cardNumber.replace(/(\d{4})/g, "$1 ")}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 3,
                  width: "100%",
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: "14px", fontWeight: "500" }}>
                    Card Holder
                  </Typography>
                  <Typography>{card.cardName}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "14px", fontWeight: "500" }}>
                    Expiry
                  </Typography>
                  <Typography>{card.expiry}</Typography>
                </Box>
              </Box>
            </Box>

            {/* Back Side */}
            <Box
              sx={{
                position: "absolute",
                width: "100%",
                height: "100%",
                backfaceVisibility: "hidden",
                background: cardGradients[cardType],
                color: "white",
                borderRadius: "12px",
                p: 2,
                transform: "rotateY(180deg)",
              }}
            >
              <Typography sx={{ fontSize: "14px", fontWeight: "500" }}>
                Security Code
              </Typography>
              <Box sx={{ bgcolor: "black", height: "40px", mt: 1 }} />
              <Box sx={{ mt: 4, textAlign: "right", pr: 2 }}>
                <Typography sx={{ fontSize: "14px" }}>
                  CVV: {card.cvv}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          p: 2,
          background: "#F2F6F9",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            px: 3,
            backgroundColor: "#0B57D0",
          }}
        >
          Save
        </Button>
      </Box>
    </Dialog>
  );
};

export default CardPreviewModal;
